import { ref, reactive, onUnmounted } from 'vue'
import { WebSocketSignaling } from '@/core/network/WebSocketSignaling'

export interface Player {
  id: string
  name: string
  pseudo: string
  avatar: string
  firstName: string
  status: 'connected' | 'loading' | 'ready'
  joinedAt: number
  score: number
  isReady: boolean
  loadingProgress?: number
  // Server-managed scoring data
  correctAnswers?: number
  fastestAnswers?: number
  completedQuestions?: number
}

export function useHostSignaling(hostId: string) {
  // Reactive state
  const isConnected = ref(false)
  const connectedPlayers = reactive(new Map<string, Player>())
  const connectionError = ref<string | null>(null)
  const signaling = ref<WebSocketSignaling | null>(null)
  const currentPartyId = ref<string | null>(null)
  const realTimeScoring = ref(false)
  const autoReveal = ref(true) // Default to enabled

  // Answer stats for current screen (for auto-reveal logic)
  const answerStats = ref({ received: 0, total: 0 })
  const currentScreenIndex = ref<number | null>(null)
  const currentGameIndex = ref<number | null>(null)

  // Track per-player completion for current screen
  const playerScreenCompletion = reactive(new Map<string, boolean>())

  // Callback for auto-reveal check - will be set by parent component
  let autoRevealCallback: (() => void) | null = null

  function setAutoRevealCallback(callback: () => void) {
    autoRevealCallback = callback
  }

  // Initialize signaling with party ID
  async function initializeSignaling(partyId: string) {
    if (!partyId || partyId.trim() === '') {
      throw new Error('Valid party ID is required')
    }

    try {
      currentPartyId.value = partyId
      signaling.value = WebSocketSignaling.getInstance()

      // Connect to WebSocket server
      await signaling.value.connect()

      setupEventListeners()
      isConnected.value = true
      connectionError.value = null
      console.log('Host signaling initialized for party:', partyId)
    } catch (error) {
      connectionError.value = error instanceof Error ? error.message : 'Unknown error'
      console.error('Failed to initialize host signaling:', error)
      throw error
    }
  }

  // Setup event listeners for player events
  function setupEventListeners() {
    if (!signaling.value) return

    // Listen for party-message responses from server
    signaling.value.on('party-message', (message: any) => {
      if (!message.data) return

      console.log(`[HOST] Received party-message:`, message.data.type)

      switch (message.data.type) {
        case 'party-created':
          console.log('Party created successfully:', message.data.partyId)
          break
        case 'party-started':
          console.log('Party started with games:', message.data.games)
          break
        case 'game-started':
          console.log('Game started:', message.data.gameIndex, message.data.gameType)
          break
        case 'game-ended':
          console.log('Game ended:', message.data.gameIndex)
          break
        case 'screen-changed':
          console.log('Screen changed:', message.data.gameIndex, message.data.screenIndex)
          break
        case 'player-joined':
          handlePlayerJoin(message.data)
          break
        case 'player-left':
          handlePlayerLeft(message.data)
          break
        case 'player-answer-received':
          handlePlayerAnswer(message.data)
          break
        case 'player-drag-match-answer-received':
          handlePlayerAnswer(message.data)
          break
        case 'player-ready-changed':
          handlePlayerStateUpdate(message.data)
          break
        case 'party-state':
          handlePartyStateUpdate(message.data)
          break
        default:
          console.log('Unhandled party message type:', message.data.type)
      }
    })

    // Legacy event listeners for transition period
    signaling.value.on('player-joined', (data: any) => {
      handlePlayerJoin(data)
    })

    signaling.value.on('player-left', (data: any) => {
      handlePlayerLeft(data)
    })

    signaling.value.on('player-loading-state', (data: any) => {
      handlePlayerStateUpdate(data)
    })
  }

  // Handle player join
  function handlePlayerJoin(data: any) {
    const { playerId, playerName, pseudo, avatar, firstName } = data

    if (!connectedPlayers.has(playerId)) {
      connectedPlayers.set(playerId, {
        id: playerId,
        name: playerName,
        pseudo: pseudo || playerName,
        avatar: avatar || '😊',
        firstName: firstName || '',
        status: 'connected',
        joinedAt: Date.now(),
        score: 0,
        isReady: false,
        correctAnswers: 0,
        fastestAnswers: 0
      })

      console.log('Player joined:', playerName)
    }
  }

  // Handle player state updates
  function handlePlayerStateUpdate(data: any) {
    const { playerId, playerName, isReady, overallProgress, assetsCompleted, totalAssets, readyFor } = data

    if (connectedPlayers.has(playerId)) {
      const player = connectedPlayers.get(playerId)!
      player.isReady = isReady
      player.loadingProgress = overallProgress
      player.status = isReady ? 'ready' : 'loading'

      // Update the Map to trigger Vue reactivity
      connectedPlayers.set(playerId, { ...player })

      console.log(`Player ${playerName} ready state:`, isReady, 'for:', readyFor)
    }
  }

  // Handle player answer submissions
  function handlePlayerAnswer(data: any) {
    const {
      playerId,
      playerName,
      firstName,
      pseudo,
      avatar,
      questionIndex,
      selectedAnswer,
      timeSpent,
      isCorrect,
      pointsAwarded,
      isFastestCorrect,
      // Server-calculated totals (don't calculate these on host side)
      totalScore,
      correctAnswers,
      fastestAnswers,
      // Drag-and-match specific fields
      screenIndex,
      isComplete,
      currentCorrectAnswers,
      currentTotalAnswers,
      hasTimerBonus,
      bonusMultiplier,
      timerDuration,
      // Drag-and-match specific totals
      totalCorrectPlacements,
      totalCompletedQuestions,
      totalSpeedBonuses,
      totalTimerBonuses
    } = data

    if (connectedPlayers.has(playerId)) {
      const player = connectedPlayers.get(playerId)!

      // Update player info if provided (in case it wasn't set during join)
      if (firstName !== undefined && firstName !== player.firstName) {
        player.firstName = firstName
      }
      if (pseudo !== undefined && pseudo !== player.pseudo) {
        player.pseudo = pseudo
      }
      if (avatar !== undefined && avatar !== player.avatar) {
        player.avatar = avatar
      }

      // Use server-calculated total score instead of adding points locally
      if (totalScore !== undefined) {
        player.score = totalScore
        // Update correct answers/placements based on game type
        player.correctAnswers = correctAnswers || totalCorrectPlacements || player.correctAnswers || 0
        player.fastestAnswers = fastestAnswers || totalSpeedBonuses || player.fastestAnswers || 0
        player.completedQuestions = totalCompletedQuestions || player.completedQuestions || 0
        connectedPlayers.set(playerId, { ...player })
      }

      const displayName = firstName ? `${firstName} (${pseudo || playerName})` : (playerName || pseudo)
      console.log(`Player ${displayName} answered Q${questionIndex || screenIndex}:`,
        selectedAnswer,
        `(${isCorrect ? 'correct' : 'incorrect'}, +${pointsAwarded} pts${isFastestCorrect ? ' FASTEST' : ''})`,
        `Total: ${totalScore || player.score} pts`)

      // Track completion for current screen (drag-and-match specific)
      if (screenIndex !== undefined && currentScreenIndex.value === screenIndex) {
        console.log(`📊 Tracking completion for player ${playerId} on screen ${screenIndex}: ${isComplete ? 'COMPLETE' : 'incomplete'}`)
        playerScreenCompletion.set(playerId, isComplete === true)
        updateAnswerStats()
      }

      // Trigger real-time scoring update if enabled
      if (realTimeScoring.value) {
        console.log('🔄 Real-time scoring is ENABLED - triggering party state request')
        requestPartyState()
      } else {
        console.log('⏸️ Real-time scoring is disabled - skipping party state request')
      }

      // Trigger auto-reveal check if enabled and callback is set
      console.log('🎯 Checking auto-reveal conditions:')
      console.log('🎯 - autoReveal.value:', autoReveal.value)
      console.log('🎯 - autoRevealCallback exists:', !!autoRevealCallback)

      if (autoReveal.value && autoRevealCallback) {
        console.log('🎯 Auto-reveal is ENABLED - checking if all players completed')
        autoRevealCallback()
      } else {
        console.log('🎯 Auto-reveal skipped - either disabled or no callback set')
      }
    }
  }

  // Handle party state updates (for real-time scoring)
  function handlePartyStateUpdate(data: any) {
    console.log('📊 Received party state update:', data)
    console.log('📊 Data keys:', Object.keys(data))
    console.log('📊 Party state:', data.partyState)

    const partyState = data.partyState
    if (!partyState) {
      console.log('⚠️ No partyState in response')
      return
    }

    console.log('📊 Players data:', partyState.players)
    console.log('📊 Scores data:', partyState.scores)
    console.log('📊 Current connectedPlayers size:', connectedPlayers.size)

    // Update connected players with latest state, merging score data
    if (partyState.players) {
      for (const [playerId, playerData] of Object.entries(partyState.players)) {
        console.log('📊 Processing player:', playerId, playerData)

        // Get score data for this player
        const scoreData = partyState.scores?.[playerId] || {}
        console.log('📊 Score data for player:', playerId, scoreData)

        if (connectedPlayers.has(playerId)) {
          const currentPlayer = connectedPlayers.get(playerId)!
          // Merge player data with score data
          const updatedPlayer = {
            ...currentPlayer,
            ...(playerData as any),
            // Add score information
            score: scoreData.totalScore || 0,
            correctAnswers: scoreData.correctAnswers || 0,
            fastestAnswers: scoreData.fastestAnswers || 0,
            playerId // Ensure playerId is preserved
          }
          connectedPlayers.set(playerId, updatedPlayer)
          console.log('📈 Updated player state:', updatedPlayer.name || updatedPlayer.pseudo,
                      `Score: ${updatedPlayer.score}, Correct: ${updatedPlayer.correctAnswers}, Fastest: ${updatedPlayer.fastestAnswers}`)
        } else {
          console.log('⚠️ Player not found in connectedPlayers:', playerId)
        }
      }
    } else {
      console.log('⚠️ No players data in party state')
    }
  }

  // Handle player left
  function handlePlayerLeft(data: any) {
    const { playerId } = data
    const player = connectedPlayers.get(playerId)

    if (player) {
      connectedPlayers.delete(playerId)
      console.log('Player left:', player.name)
    }
  }

  // Send host message using new structure
  function sendHostMessage(type: string, data: any) {
    if (signaling.value) {
      const message = {
        type: 'host-message',
        data: {
          type,
          ...data
        }
      }

      console.log(`[HOST] Sending host-message:`, type, data)
      signaling.value.sendDirectMessage(message)
    } else {
      console.warn('No signaling available to send host message')
    }
  }

  // Create party with configuration
  function createParty(partyId: string, configuration: any) {
    sendHostMessage('create-party', {
      partyId,
      configuration
    })
  }

  // Start the party (transition from lobby to started)
  function startParty(games: any[]) {
    sendHostMessage('start-party', {
      games
    })
  }

  // Start a specific game
  function startGame(gameIndex: number, gameType: string, gameData: any) {
    sendHostMessage('start-game', {
      gameIndex,
      gameType,
      gameData
    })
  }

  // End a specific game
  function endGame(gameIndex: number, results: any) {
    sendHostMessage('end-game', {
      gameIndex,
      results
    })
  }

  // Change screen within a game
  function changeScreen(gameIndex: number, screenIndex: number, screenData: any) {
    sendHostMessage('change-screen', {
      gameIndex,
      screenIndex,
      screenData
    })
  }

  // Display score update
  function displayScoreUpdate(scoreData: any) {
    sendHostMessage('display-score-update', {
      scoreData
    })
  }

  // Request current party state (for real-time scoring updates)
  function requestPartyState() {
    if (signaling.value && currentPartyId.value) {
      const message = {
        type: 'get-party-state',
        partyId: currentPartyId.value
      }

      console.log('🔄 Requesting party state update for real-time scoring')
      signaling.value.sendDirectMessage(message)
    } else {
      console.warn('Cannot request party state - no signaling or party ID available')
    }
  }

  // Legacy broadcast method - kept for backward compatibility
  function broadcastMessage(type: string, data: any) {
    console.log('📡 HOST COMPOSABLE: Legacy broadcast message:', type, 'with data:', data)
    console.log('⚠️ HOST COMPOSABLE: Consider using specific host message methods instead')

    if (signaling.value) {
      try {
        signaling.value.sendPartyMessage({ type, ...data })
        console.log('✅ HOST COMPOSABLE: Message sent via legacy broadcast')
      } catch (error) {
        console.error('❌ HOST COMPOSABLE: Error sending message:', error)
      }
    } else {
      console.warn('⚠️ HOST COMPOSABLE: No signaling instance available')
    }
  }

  // Send game state updates - LEGACY (use changeScreen or displayScoreUpdate instead)
  function broadcastGameState(gameData: any) {
    console.warn('⚠️ broadcastGameState is legacy - use changeScreen() or displayScoreUpdate() instead')
    if (signaling.value && isConnected.value) {
      signaling.value.sendPartyMessage({ type: 'game-state', ...gameData })
    }
  }

  // Send party configuration using NEW SPECIFICATION
  function sendPartyConfiguration(configuration: any) {
    if (signaling.value && currentPartyId.value) {
      createParty(currentPartyId.value, configuration)
      console.log('Party configuration sent to server via NEW SPECIFICATION:', configuration)
    } else {
      console.warn('No signaling available or no party ID to send party configuration')
    }
  }

  // Reveal answers for current screen/question
  function revealAnswers() {
    if (signaling.value && currentPartyId.value) {
      console.log('🎉 Revealing answers for current screen...')
      sendHostMessage('reveal-answers', {})
    } else {
      console.warn('No signaling available or no party ID to reveal answers')
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    signaling.value?.disconnect?.()
  })

  // Update current screen tracking for answer stats
  function updateCurrentScreen(gameIndex: number, screenIndex: number) {
    currentGameIndex.value = gameIndex
    currentScreenIndex.value = screenIndex
    // Clear completion tracking when screen changes
    playerScreenCompletion.clear()
    answerStats.value = { received: 0, total: 0 }
    updateAnswerStats()
    console.log(`📊 Updated current screen: game ${gameIndex}, screen ${screenIndex}`)
    console.log(`📊 Cleared completion tracking for new screen`)
  }

  // Calculate answer stats for current screen
  function updateAnswerStats() {
    if (currentScreenIndex.value === null) {
      answerStats.value = { received: 0, total: 0 }
      return
    }

    const activePlayerCount = connectedPlayers.size
    let completedCount = 0

    // Count players who have completed the current screen
    Array.from(connectedPlayers.keys()).forEach(playerId => {
      const isCompleted = playerScreenCompletion.get(playerId) === true
      if (isCompleted) {
        completedCount++
      }
    })

    answerStats.value = {
      received: completedCount,
      total: activePlayerCount
    }

    console.log(`📊 Answer stats updated: ${completedCount}/${activePlayerCount} completed current screen ${currentScreenIndex.value}`)
    console.log(`📊 Completion map:`, Array.from(playerScreenCompletion.entries()))
  }

  return {
    // State
    isConnected,
    connectedPlayers,
    connectionError,
    currentPartyId,
    realTimeScoring,
    autoReveal,
    answerStats,

    // Methods - NEW SPECIFICATION
    initializeSignaling,
    createParty,
    startParty,
    startGame,
    endGame,
    changeScreen,
    displayScoreUpdate,
    requestPartyState,
    sendHostMessage,
    revealAnswers,
    setAutoRevealCallback,
    updateCurrentScreen,

    // Methods - Legacy support
    sendPartyConfiguration,
    broadcastMessage,
    broadcastGameState,

    // Computed getters
    playerCount: () => connectedPlayers.size,
    readyPlayerCount: () => Array.from(connectedPlayers.values()).filter(p => p.isReady).length,
    allPlayersReady: () => connectedPlayers.size > 0 && Array.from(connectedPlayers.values()).every(p => p.isReady)
  }
}
